import React, { Fragment, useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import Header from '../../../components/Header';
import Appfooter from '../../../components/Appfooter';
import Load from '../../../components/Load';
import {
  AGENDAMENTOS_POR_QUADRA_E_DATA,
  CRIAR_AGENDAMENTO_ADMIN,
  EDITAR_AGENDAMENTO,
  CANCELAR_AGENDAMENTO_ADMIN,
  CRIAR_BLOQUEIO,
  DELETAR_BLOQUEIO,
  BLOQUEIOS_POR_QUADRA_E_DATA,
  BUSCAR_USUARIOS,
  CONFIRMAR_PAGAMENTO_ADMIN,
  gerarHorarios,
} from '../../../services/quadraAdminService';
import {
  QUADRA_POR_ID,
  DISPONIBILIDADE_QUADRA,
  CALCULAR_VALOR,
  STATUS_LABEL,
  formatCurrency,
  todayISO,
} from '../../../services/quadraService';

const HORARIOS = gerarHorarios();
const DIAS_PT = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const formatTelefone = (tel?: string | null) => {
  if (!tel) return '';
  const digits = tel.replace(/\D/g, '');
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return tel;
};

type Modal = 'agendamento' | 'editar' | 'bloqueio' | 'cancelar' | null;

const AdminAgenda: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(searchParams.get('data') ?? todayISO());
  const [modal, setModal] = useState<Modal>(null);
  const [agSelecionado, setAgSelecionado] = useState<any | null>(null);
  const [erro, setErro] = useState('');
  const [conflitos, setConflitos] = useState<any[]>([]);

  // Forms
  const [formAg, setFormAg] = useState({ usuarioId: '', horaInicio: searchParams.get('horaInicio') ?? '08:00', horaFim: searchParams.get('horaFim') ?? '09:00', valor: '', observacao: '', forcarSobreposicao: false });
  const [buscaUsuario, setBuscaUsuario] = useState('');
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<{ id: string; nome: string; email: string } | null>(null);
  const [buscarUsuarios, { data: usuariosData, loading: buscandoUsuarios }] = useLazyQuery(BUSCAR_USUARIOS, { fetchPolicy: 'network-only' });

  useEffect(() => {
    if (buscaUsuario.length < 2) return;
    const t = setTimeout(() => buscarUsuarios({ variables: { nome: buscaUsuario } }), 350);
    return () => clearTimeout(t);
  }, [buscaUsuario, buscarUsuarios]);

  const selecionarUsuario = (u: { id: string; nome: string; email: string }) => {
    setUsuarioSelecionado(u);
    setFormAg(f => ({ ...f, usuarioId: u.id }));
    setBuscaUsuario('');
  };

  const limparUsuario = () => {
    setUsuarioSelecionado(null);
    setFormAg(f => ({ ...f, usuarioId: '' }));
  };

  // Auto-open modal de agendamento se URL tem horaInicio e horaFim
  useEffect(() => {
    const hi = searchParams.get('horaInicio');
    const hf = searchParams.get('horaFim');
    if (hi && hf) {
      setFormAg(f => ({ ...f, horaInicio: hi, horaFim: hf }));
      setModal('agendamento');
      // Limpa params da URL para não reabrir ao navegar
      const p = new URLSearchParams(searchParams);
      p.delete('horaInicio');
      p.delete('horaFim');
      setSearchParams(p, { replace: true });
    }
  }, []); // eslint-disable-line
  const [formEdit, setFormEdit] = useState({ horaInicio: '', horaFim: '', valor: '', observacao: '' });
  const [formBloqueio, setFormBloqueio] = useState({ horaInicio: '08:00', horaFim: '12:00', motivo: '', forcar: false });
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [resultadoCancelamento, setResultadoCancelamento] = useState<{ multaAplicada: number } | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'confirmadas' | 'pendentes' | 'canceladas'>('todos');

  const { data: quadraData } = useQuery(QUADRA_POR_ID, { variables: { id } });
  const { data: agData, loading: loadingAg, error: erroAg, refetch } = useQuery(AGENDAMENTOS_POR_QUADRA_E_DATA, {
    variables: { quadraId: id, data },
    fetchPolicy: 'network-only',
  });
  const { data: bloqueiosData, refetch: refetchBloqueios } = useQuery(BLOQUEIOS_POR_QUADRA_E_DATA, {
    variables: { quadraId: id, data },
    fetchPolicy: 'network-only',
  });
  const [buscarDisp, { data: dispData }] = useLazyQuery(DISPONIBILIDADE_QUADRA, { fetchPolicy: 'network-only' });
  const [calcularValor, { data: valorData, loading: calculandoValor }] = useLazyQuery(CALCULAR_VALOR, { fetchPolicy: 'network-only' });

  const [criarAg, { loading: criandoAg }] = useMutation(CRIAR_AGENDAMENTO_ADMIN);
  const [editarAg, { loading: editandoAg }] = useMutation(EDITAR_AGENDAMENTO);
  const [cancelarAg, { loading: cancelando }] = useMutation(CANCELAR_AGENDAMENTO_ADMIN);
  const [confirmarPagamento] = useMutation(CONFIRMAR_PAGAMENTO_ADMIN);
  const [criarBloqueio, { loading: bloqueando }] = useMutation(CRIAR_BLOQUEIO);
  const [deletarBloqueio] = useMutation(DELETAR_BLOQUEIO);

  useEffect(() => {
    if (id && data) buscarDisp({ variables: { quadraId: id, data } });
  }, [id, data, buscarDisp]);

  const agendamentos = agData?.agendamentosPorQuadraEData ?? [];
  const bloqueios: any[] = bloqueiosData?.bloqueiosPorQuadraEData ?? [];
  const slots = dispData?.disponibilidadeQuadra ?? [];
  const quadra = quadraData?.quadraPorId;
  const diaSemana = DIAS_PT[new Date(data + 'T12:00:00').getDay()];

  const abrirAgendamentoPorSlot = (slot: any) => {
    if (!slot.disponivel) return;
    const idx = HORARIOS.indexOf(slot.horaInicio);
    const horaFim = HORARIOS[idx + 2] ?? slot.horaFim;
    setFormAg(f => ({ ...f, horaInicio: slot.horaInicio, horaFim, valor: '' }));
    calcularValor({ variables: { quadraId: id, dataAgendamento: data, horaInicio: slot.horaInicio, horaFim } });
    setErro('');
    setConflitos([]);
    setModal('agendamento');
  };

  useEffect(() => {
    if (modal !== 'agendamento') return;
    calcularValor({ variables: { quadraId: id, dataAgendamento: data, horaInicio: formAg.horaInicio, horaFim: formAg.horaFim } });
  }, [formAg.horaInicio, formAg.horaFim, modal, id, data, calcularValor]);

  useEffect(() => {
    const v = valorData?.calcularValorAgendamento;
    if (v != null) setFormAg(f => ({ ...f, valor: String(v) }));
  }, [valorData]);

  const handleConfirmarPagamento = async (ag: any) => {
    try {
      await confirmarPagamento({ variables: { id: ag.id } });
      refetch();
    } catch (err: any) {
      alert(err?.graphQLErrors?.[0]?.message ?? err.message);
    }
  };

  const abrirEditar = (ag: any) => {
    setAgSelecionado(ag);
    setFormEdit({ horaInicio: ag.horaInicio, horaFim: ag.horaFim, valor: String(ag.valor), observacao: ag.observacao ?? '' });
    setModal('editar');
    setErro('');
  };

  const abrirCancelar = (ag: any) => {
    setAgSelecionado(ag);
    setMotivoCancelamento('');
    setModal('cancelar');
    setErro('');
  };

  const handleCriarAg = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    setConflitos([]);

    if (formAg.horaFim <= formAg.horaInicio) {
      setErro('O horário de fim deve ser depois do horário de início.');
      return;
    }
    if (buscaUsuario.trim() && !usuarioSelecionado) {
      setErro('Selecione um usuário da lista ou limpe o campo de busca.');
      return;
    }
    if (formAg.valor === '' || parseFloat(formAg.valor) < 0) {
      setErro('O valor deve ser maior ou igual a zero.');
      return;
    }

    try {
      await criarAg({
        variables: {
          input: {
            quadraId: id,
            dataAgendamento: data,
            horaInicio: formAg.horaInicio,
            horaFim: formAg.horaFim,
            valor: parseFloat(formAg.valor),
            observacao: formAg.observacao,
            usuarioId: formAg.usuarioId || undefined,
            forcarSobreposicao: formAg.forcarSobreposicao,
          },
        },
      });
      setModal(null);
      refetch();
      buscarDisp({ variables: { quadraId: id, data } });
    } catch (err: any) {
      const msg = err?.graphQLErrors?.[0]?.message ?? err.message;
      setErro(msg);
    }
  };

  const handleEditarAg = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    try {
      await editarAg({
        variables: {
          id: agSelecionado.id,
          input: { quadraId: id, dataAgendamento: data, horaInicio: formEdit.horaInicio, horaFim: formEdit.horaFim, valor: parseFloat(formEdit.valor), observacao: formEdit.observacao },
        },
      });
      setModal(null);
      refetch();
    } catch (err: any) {
      const msg = err?.graphQLErrors?.[0]?.message ?? err.message;
      setErro(msg);
    }
  };

  const handleCancelar = async () => {
    setErro('');
    try {
      const { data: res } = await cancelarAg({ variables: { id: agSelecionado.id, motivo: motivoCancelamento || undefined } });
      const multa = res?.cancelarAgendamento?.multaAplicada ?? 0;
      setResultadoCancelamento({ multaAplicada: multa });
      refetch();
      refetchBloqueios();
      buscarDisp({ variables: { quadraId: id, data } });
    } catch (err: any) {
      const msg = err?.graphQLErrors?.[0]?.message ?? err.message;
      setErro(msg);
    }
  };

  const handleBloqueio = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    setConflitos([]);
    try {
      const { data: res } = await criarBloqueio({
        variables: { input: { quadraId: id, dataBloqueio: data, ...formBloqueio } },
      });
      const result = res.criarBloqueioAgenda;
      if (result.conflitos?.length > 0 && !formBloqueio.forcar) {
        setConflitos(result.conflitos);
        setErro(`Existem ${result.conflitos.length} agendamento(s) conflitantes. Marque "Forçar" para criar mesmo assim.`);
        return;
      }
      setModal(null);
      refetch();
      buscarDisp({ variables: { quadraId: id, data } });
    } catch (err: any) {
      const msg = err?.graphQLErrors?.[0]?.message ?? err.message;
      setErro(msg);
    }
  };

  const handleDesbloquear = async (bloqueioId: string) => {
    if (!window.confirm('Desbloquear este horário?')) return;
    try {
      await deletarBloqueio({ variables: { id: bloqueioId } });
      refetchBloqueios();
      buscarDisp({ variables: { quadraId: id, data } });
    } catch (err: any) {
      alert(err?.graphQLErrors?.[0]?.message ?? err.message);
    }
  };

  const fecharModal = () => { setModal(null); setErro(''); setConflitos([]); setBuscaUsuario(''); setUsuarioSelecionado(null); setResultadoCancelamento(null); };

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">

            {/* Header */}
            <div className="card shadow-xss w-100 border-0 p-4 mb-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <button className="btn btn-light btn-sm rounded-xl" onClick={() => navigate(-1)}>
                  <i className="feather-arrow-left me-1"></i> Voltar
                </button>
                <div>
                  <h2 className="fw-700 font-md text-grey-900 mb-0">Agenda da Quadra</h2>
                  {quadra && <span className="font-xssss text-grey-500">{quadra.local?.nome} / {quadra.nome}</span>}
                </div>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <button type="button" className="btn btn-light rounded-xl px-2" style={{ height: 38 }}
                  onClick={() => { const d = new Date(data + 'T12:00:00'); d.setDate(d.getDate() - 1); setData(d.toISOString().split('T')[0]); }}>
                  <i className="feather-chevron-left"></i>
                </button>
                <input type="date" className="form-control rounded-xl font-xsss" value={data}
                  onChange={e => setData(e.target.value)} style={{ width: 160, height: 38 }} />
                <button type="button" className="btn btn-light rounded-xl px-2" style={{ height: 38 }}
                  onClick={() => { const d = new Date(data + 'T12:00:00'); d.setDate(d.getDate() + 1); setData(d.toISOString().split('T')[0]); }}>
                  <i className="feather-chevron-right"></i>
                </button>
                <span className="font-xssss text-grey-500">{diaSemana}</span>
                <div className="ms-auto d-flex gap-2">
                  <button className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3"
                    onClick={() => { setModal('agendamento'); setErro(''); setConflitos([]); }}>
                    <i className="feather-plus me-1"></i> Agendamento manual
                  </button>
                  <button className="btn btn-outline-warning rounded-xl font-xssss fw-600 px-3"
                    onClick={() => { setModal('bloqueio'); setErro(''); setConflitos([]); }}>
                    <i className="feather-lock me-1"></i> Bloquear horário
                  </button>
                  <button className="btn btn-light rounded-xl font-xssss fw-600 px-3"
                    onClick={() => navigate(`/admin/quadras/${id}/relatorio`)}>
                    <i className="feather-bar-chart-2 me-1"></i> Relatório
                  </button>
                </div>
              </div>
            </div>

            <div className="row">
              {/* Grade de horários */}
              <div className="col-lg-4 mb-3">
                <div className="card shadow-xss rounded-xxl border-0 p-4">
                  <h5 className="fw-700 font-xss mb-3">Disponibilidade</h5>
                  {slots.length === 0 && <p className="text-grey-500 font-xssss">Nenhum horário para esta data.</p>}
                  <div className="d-flex flex-wrap gap-2">
                    {slots.map((slot: any, i: number) => {
                      const cor = slot.disponivel
                        ? 'bg-success'
                        : slot.motivo === 'BLOQUEADO'
                        ? 'bg-secondary'
                        : 'bg-warning';
                      return (
                        <span
                          key={i}
                          onClick={() => abrirAgendamentoPorSlot(slot)}
                          className={`badge rounded-xl font-xssss px-2 py-1 ${cor}`}
                          style={{ cursor: slot.disponivel ? 'pointer' : 'default' }}
                          title={slot.disponivel ? 'Clique para agendar' : slot.motivo === 'BLOQUEADO' ? 'Bloqueado' : 'Reservado'}
                        >
                          {slot.horaInicio}
                        </span>
                      );
                    })}
                  </div>
                  <div className="d-flex gap-3 mt-3">
                    <span className="d-flex align-items-center gap-1 font-xssss text-grey-500">
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: '#22c55e', display: 'inline-block' }}></span> Disponível
                    </span>
                    <span className="d-flex align-items-center gap-1 font-xssss text-grey-500">
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: '#f59e0b', display: 'inline-block' }}></span> Reservado
                    </span>
                    <span className="d-flex align-items-center gap-1 font-xssss text-grey-500">
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: '#6b7280', display: 'inline-block' }}></span> Bloqueado
                    </span>
                  </div>
                </div>
              </div>

              {/* Lista de agendamentos */}
              <div className="col-lg-8">
                <div className="card shadow-xss rounded-xxl border-0 p-4">
                  <h5 className="fw-700 font-xss mb-3">Agendamentos da quadra</h5>

                  {/* Filtros de status */}
                  <div className="d-flex gap-2 mb-3 flex-wrap">
                    {(['todos', 'confirmadas', 'pendentes', 'canceladas'] as const).map(f => (
                      <button
                        key={f}
                        className={`btn rounded-xl font-xssss fw-600 px-3 py-1 ${filtroStatus === f ? 'bg-primary-gradiant text-white' : 'btn-light'}`}
                        onClick={() => setFiltroStatus(f)}
                      >
                        {f === 'todos' && 'Todos'}
                        {f === 'confirmadas' && 'Confirmadas'}
                        {f === 'pendentes' && 'Pendentes'}
                        {f === 'canceladas' && 'Canceladas'}
                      </button>
                    ))}
                  </div>
                  {bloqueios.length > 0 && (
                    <div className="mb-3">
                      <h6 className="fw-600 font-xssss text-warning mb-2"><i className="feather-lock me-1"></i>Horários bloqueados</h6>
                      {bloqueios.map((b: any) => (
                        <div key={b.id} className="d-flex align-items-center justify-content-between p-2 mb-1 rounded-xl" style={{ background: '#fff8e1' }}>
                          <span className="font-xssss fw-600">{b.horaInicio} – {b.horaFim}</span>
                          {b.motivo && <span className="font-xssss text-grey-500 mx-2 flex-grow-1">{b.motivo}</span>}
                          <button className="btn btn-outline-warning rounded-xl font-xssss px-2 py-1" onClick={() => handleDesbloquear(b.id)}>
                            <i className="feather-unlock me-1"></i>Desbloquear
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {loadingAg && <Load />}
                  {erroAg && <div className="alert alert-danger font-xssss py-2">{erroAg.message}</div>}
                  {!loadingAg && agendamentos.length === 0 && (
                    <p className="text-grey-500 font-xssss text-center py-3">Nenhum agendamento para esta data.</p>
                  )}
                  {[...agendamentos]
                    .filter((ag: any) => {
                      if (filtroStatus === 'confirmadas') return ag.status === 'CONFIRMADO';
                      if (filtroStatus === 'pendentes') return ['RESERVADO', 'A_PAGAR'].includes(ag.status);
                      if (filtroStatus === 'canceladas') return ag.status === 'CANCELADO';
                      return true;
                    })
                    .sort((a: any, b: any) => a.horaInicio.localeCompare(b.horaInicio)).map((ag: any) => {
                    const st = STATUS_LABEL[ag.status] ?? { label: ag.status, color: 'secondary' };
                    return (
                      <div key={ag.id} className="d-flex align-items-center p-3 border-bottom gap-3">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="font-xsss fw-700">{ag.horaInicio} – {ag.horaFim}</span>
                            <span className={`badge bg-${st.color} font-xssss rounded-xl`}>{st.label}</span>
                          </div>
                          <p className="font-xssss fw-600 text-grey-900 mb-0">
                            <i className="feather-user me-1" style={{ fontSize: 11 }}></i>
                            {ag.usuario?.nome ?? 'Usuário não informado'}
                          </p>
                          <p className="font-xssss text-grey-500 mb-0">
                            {ag.usuario?.email && <><i className="feather-mail me-1" style={{ fontSize: 10 }}></i>{ag.usuario.email}</>}
                            {ag.usuario?.atleta?.telefone && <span className="ms-2"><i className="feather-phone me-1" style={{ fontSize: 10 }}></i>{formatTelefone(ag.usuario.atleta.telefone)}</span>}
                          </p>
                          {ag.observacao && <p className="font-xssss text-grey-400 mb-0 mt-1"><em>{ag.observacao}</em></p>}
                          <span className="font-xssss fw-700 text-success">{ag.valor != null ? formatCurrency(ag.valor) : '—'}</span>
                        </div>
                        <div className="d-flex gap-1 flex-shrink-0">
                          {['RESERVADO', 'A_PAGAR'].includes(ag.status) && (
                            <button className="btn btn-outline-success rounded-xl font-xssss px-2 py-1" title="Confirmar pagamento" onClick={() => handleConfirmarPagamento(ag)}>
                              <i className="feather-check"></i>
                            </button>
                          )}
                          {['RESERVADO', 'A_PAGAR', 'CONFIRMADO'].includes(ag.status) && (
                            <>
                              <button className="btn btn-light rounded-xl font-xssss px-2 py-1" title="Editar" onClick={() => abrirEditar(ag)}>
                                <i className="feather-edit-2"></i>
                              </button>
                              <button className="btn btn-outline-danger rounded-xl font-xssss px-2 py-1" title="Cancelar" onClick={() => abrirCancelar(ag)}>
                                <i className="feather-x"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Modais ── */}
      {modal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
            <div className="modal-content rounded-xxl border-0 shadow-lg p-4">

              {/* Agendamento manual */}
              {modal === 'agendamento' && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="fw-700 font-xss mb-0">Agendamento Manual</h5>
                    <button className="btn-close" onClick={fecharModal} />
                  </div>
                  {quadra && (
                    <div className="alert alert-light py-2 mb-3 font-xssss">
                      <i className="feather-map-pin me-1 text-primary"></i>
                      <strong>{quadra.nome}</strong>
                      <span className="text-grey-500 ms-2">
                        {new Date(data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })} &mdash; {diaSemana}
                      </span>
                    </div>
                  )}
                  <form onSubmit={handleCriarAg}>
                    <div className="mb-2 position-relative">
                      <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Usuário (opcional)</label>
                      {usuarioSelecionado ? (
                        <div className="d-flex align-items-center gap-2 border rounded-xl px-3 py-2 bg-light">
                          <span className="font-xssss flex-grow-1">
                            <strong>{usuarioSelecionado.nome}</strong> <span className="text-grey-500">{usuarioSelecionado.email}</span>
                          </span>
                          <button type="button" className="btn-close btn-close-sm" onClick={limparUsuario} />
                        </div>
                      ) : (
                        <>
                          <input
                            className="form-control rounded-xl font-xsss"
                            value={buscaUsuario}
                            onChange={e => setBuscaUsuario(e.target.value)}
                            placeholder="Digite o nome do usuário..."
                            autoComplete="off"
                          />
                          {buscandoUsuarios && <small className="text-grey-500 font-xssss">Buscando...</small>}
                          {!buscandoUsuarios && buscaUsuario.length >= 2 && usuariosData?.usuariosPorNome?.length === 0 && (
                            <small className="text-grey-500 font-xssss">Nenhum usuário encontrado.</small>
                          )}
                          {(usuariosData?.usuariosPorNome?.length ?? 0) > 0 && buscaUsuario.length >= 2 && (
                            <div className="border rounded-xl shadow-sm bg-white position-absolute w-100" style={{ zIndex: 100, top: '100%', maxHeight: 180, overflowY: 'auto' }}>
                              {usuariosData.usuariosPorNome.map((u: any) => (
                                <div
                                  key={u.id}
                                  className="px-3 py-2 font-xssss cursor-pointer"
                                  style={{ cursor: 'pointer' }}
                                  onMouseDown={() => selecionarUsuario(u)}
                                >
                                  <strong>{u.nome}</strong> <span className="text-grey-500">{u.email}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Início</label>
                        <select className="form-select rounded-xl font-xsss" value={formAg.horaInicio}
                          onChange={e => setFormAg(f => ({ ...f, horaInicio: e.target.value }))}>
                          {HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Fim</label>
                        <select className="form-select rounded-xl font-xsss" value={formAg.horaFim}
                          onChange={e => setFormAg(f => ({ ...f, horaFim: e.target.value }))}>
                          {HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">
                        Valor (R$) * {calculandoValor && <span className="text-grey-400">(calculando...)</span>}
                      </label>
                      <input type="number" step="0.01" min="0" className="form-control rounded-xl font-xsss"
                        value={formAg.valor} onChange={e => setFormAg(f => ({ ...f, valor: e.target.value }))} />
                    </div>
                    <div className="mb-2">
                      <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Observação *</label>
                      <textarea className="form-control rounded-xl font-xsss" rows={2}
                        value={formAg.observacao} onChange={e => setFormAg(f => ({ ...f, observacao: e.target.value }))} />
                    </div>
                    <div className="form-check mb-3">
                      <input type="checkbox" className="form-check-input" id="forcar"
                        checked={formAg.forcarSobreposicao} onChange={e => setFormAg(f => ({ ...f, forcarSobreposicao: e.target.checked }))} />
                      <label className="form-check-label font-xssss" htmlFor="forcar">Forçar sobreposição</label>
                    </div>
                    {erro && <div className="alert alert-danger font-xssss py-2 mb-2">{erro}</div>}
                    {conflitos.length > 0 && (
                      <div className="alert alert-warning font-xssss py-2 mb-2">
                        Conflitos: {conflitos.map((c: any) => `${c.horaInicio}–${c.horaFim}`).join(', ')}
                      </div>
                    )}
                    <div className="d-flex gap-2">
                      <button type="submit" className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3" disabled={criandoAg}>
                        {criandoAg ? 'Criando...' : 'Criar agendamento'}
                      </button>
                      <button type="button" className="btn btn-light rounded-xl font-xssss fw-600 px-3" onClick={fecharModal}>Cancelar</button>
                    </div>
                  </form>
                </>
              )}

              {/* Editar agendamento */}
              {modal === 'editar' && agSelecionado && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="fw-700 font-xss mb-0">Editar Agendamento</h5>
                    <button className="btn-close" onClick={fecharModal} />
                  </div>
                  <form onSubmit={handleEditarAg}>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Início</label>
                        <select className="form-select rounded-xl font-xsss" value={formEdit.horaInicio}
                          onChange={e => setFormEdit(f => ({ ...f, horaInicio: e.target.value }))}>
                          {HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Fim</label>
                        <select className="form-select rounded-xl font-xsss" value={formEdit.horaFim}
                          onChange={e => setFormEdit(f => ({ ...f, horaFim: e.target.value }))}>
                          {HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Valor (R$)</label>
                      <input type="number" step="0.01" min="0" className="form-control rounded-xl font-xsss"
                        value={formEdit.valor} onChange={e => setFormEdit(f => ({ ...f, valor: e.target.value }))} />
                    </div>
                    <div className="mb-3">
                      <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Motivo da edição *</label>
                      <textarea className="form-control rounded-xl font-xsss" rows={2} required
                        value={formEdit.observacao} onChange={e => setFormEdit(f => ({ ...f, observacao: e.target.value }))} />
                    </div>
                    {erro && <div className="alert alert-danger font-xssss py-2 mb-2">{erro}</div>}
                    <div className="d-flex gap-2">
                      <button type="submit" className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3" disabled={editandoAg}>
                        {editandoAg ? 'Salvando...' : 'Salvar'}
                      </button>
                      <button type="button" className="btn btn-light rounded-xl font-xssss fw-600 px-3" onClick={fecharModal}>Cancelar</button>
                    </div>
                  </form>
                </>
              )}

              {/* Cancelar agendamento */}
              {modal === 'cancelar' && agSelecionado && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="fw-700 font-xss mb-0">Cancelar Agendamento</h5>
                    <button className="btn-close" onClick={fecharModal} />
                  </div>
                  {resultadoCancelamento ? (
                    <div className="text-center py-3">
                      <i className="feather-check-circle text-success" style={{ fontSize: 40 }}></i>
                      <p className="fw-600 font-xss mt-3 mb-1">Agendamento cancelado!</p>
                      {resultadoCancelamento.multaAplicada === 0 ? (
                        <div className="alert alert-success font-xssss py-2 mt-2">
                          <i className="feather-dollar-sign me-1"></i>
                          O valor foi creditado na conta do usuário.
                        </div>
                      ) : (
                        <div className="alert alert-warning font-xssss py-2 mt-2">
                          <i className="feather-alert-triangle me-1"></i>
                          Sem reembolso — cancelamento fora da janela permitida.
                        </div>
                      )}
                      <button className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-4 mt-2" onClick={fecharModal}>Fechar</button>
                    </div>
                  ) : (
                    <>
                      <p className="font-xsss text-grey-600 mb-3">
                        {agSelecionado.horaInicio} – {agSelecionado.horaFim} | {agSelecionado.usuario?.nome}
                      </p>
                      {agSelecionado.status === 'CONFIRMADO' && (
                        <div className="alert alert-warning font-xssss py-2 mb-3">
                          <i className="feather-alert-triangle me-1"></i>
                          Agendamento pago. O reembolso depende da política de cancelamento do local.
                        </div>
                      )}
                      <div className="mb-3">
                        <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Motivo (opcional)</label>
                        <textarea className="form-control rounded-xl font-xsss" rows={2}
                          value={motivoCancelamento} onChange={e => setMotivoCancelamento(e.target.value)} />
                      </div>
                      {erro && <div className="alert alert-danger font-xssss py-2 mb-2">{erro}</div>}
                      <div className="d-flex gap-2">
                        <button className="btn btn-danger rounded-xl font-xssss fw-600 px-3" onClick={handleCancelar} disabled={cancelando}>
                          {cancelando ? 'Cancelando...' : 'Confirmar cancelamento'}
                        </button>
                        <button className="btn btn-light rounded-xl font-xssss fw-600 px-3" onClick={fecharModal}>Voltar</button>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Bloqueio */}
              {modal === 'bloqueio' && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="fw-700 font-xss mb-0">Bloquear Horário</h5>
                    <button className="btn-close" onClick={fecharModal} />
                  </div>
                  <p className="font-xssss text-grey-500 mb-3">Data: {new Date(data + 'T12:00:00').toLocaleDateString('pt-BR')}</p>
                  <form onSubmit={handleBloqueio}>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Início</label>
                        <select className="form-select rounded-xl font-xsss" value={formBloqueio.horaInicio}
                          onChange={e => setFormBloqueio(f => ({ ...f, horaInicio: e.target.value }))}>
                          {HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Fim</label>
                        <select className="form-select rounded-xl font-xsss" value={formBloqueio.horaFim}
                          onChange={e => setFormBloqueio(f => ({ ...f, horaFim: e.target.value }))}>
                          {HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Motivo *</label>
                      <input className="form-control rounded-xl font-xsss" required
                        value={formBloqueio.motivo} onChange={e => setFormBloqueio(f => ({ ...f, motivo: e.target.value }))}
                        placeholder="Ex: Manutenção programada" />
                    </div>
                    <div className="form-check mb-3">
                      <input type="checkbox" className="form-check-input" id="forcarBloqueio"
                        checked={formBloqueio.forcar} onChange={e => setFormBloqueio(f => ({ ...f, forcar: e.target.checked }))} />
                      <label className="form-check-label font-xssss" htmlFor="forcarBloqueio">Forçar (mesmo com conflitos)</label>
                    </div>
                    {erro && <div className="alert alert-danger font-xssss py-2 mb-2">{erro}</div>}
                    {conflitos.length > 0 && (
                      <div className="alert alert-warning font-xssss py-2 mb-2">
                        Conflitos: {conflitos.map((c: any) => `${c.horaInicio}–${c.horaFim} (${c.status})`).join(', ')}
                      </div>
                    )}
                    <div className="d-flex gap-2">
                      <button type="submit" className="btn btn-warning rounded-xl font-xssss fw-600 px-3" disabled={bloqueando}>
                        {bloqueando ? 'Bloqueando...' : 'Criar bloqueio'}
                      </button>
                      <button type="button" className="btn btn-light rounded-xl font-xssss fw-600 px-3" onClick={fecharModal}>Cancelar</button>
                    </div>
                  </form>
                </>
              )}

            </div>
          </div>
        </div>
      )}

      <Appfooter />
    </Fragment>
  );
};

export default AdminAgenda;
